import * as React from 'react'
import { ConfigurableRelationData } from 'modules/productDetail/types'
import config from 'config'
import createAlgoliaHelper from 'utils/createAlgoliaHelper'
import * as gt from 'utils/productShapeTypes'

/** @firescoutMockFn product.fetch */
export default function useProduct(sku: string) {
  const [fetching, setFetching] = React.useState(true)
  const [product, setProduct] = React.useState<null | Product>(null)

  React.useEffect(() => {
    createAlgoliaHelper(config.index.products, {
      hitsPerPage: 1,
      facets: ['active', 'sellable'],
      disjunctiveFacets: ['sku'],
      attributesToRetrieve: [
        'sku',
        'containerID',
        'sw6Uuid',
        'prices',
        'priceRules',
        'images',
        'title',
        'channelActive',
        'brand',
        'unit',
        'flags',
        'variantImages',
        'categories',
        'attributes',
        'variantData',
        'related',
        'stock',
        'deliveryDate',
        'deliveryDays',
        'specialDelivery',
        'sellOut',
        'sellable',
        'subtitle',
        'isMainVariant',
        'description',
        'productReviews',
        'descriptionAI'
      ]
    })
      .then(async (helper) => {
        helper.addDisjunctiveFacetRefinement('sku', sku)
        helper.addFacetRefinement('active', 'true')
        helper.addFacetRefinement('sellable', 'true')
        const result = await helper.searchOnce({})

        setProduct(result.content.hits[0] ?? null)
        setFetching(false)
      })
      .catch(() => setFetching(false))
  }, [sku])

  return {
    fetching,
    data: product
  }
}

export type Product = {
  sku: string
  containerID: string
  sw6Uuid: string
  // when added we track the click to dynamic yield
  dyContext?: {
    slotId: string
    decisionId: string
  }
  description?: string
  deliveryDays: number | null
  prices: gt.ProductPriceDefinition
  priceRules: gt.ProductPriceRulesDefinition
  images: gt.ProductImagesDefinition
  title: string
  channelActive: gt.ProductChannelActiveDefinition
  brand?: string
  unit: gt.ProductUnitDefinition
  flags: string[]
  variantImages: string[]
  categories: gt.ProductCategoriesDefinition
  attributes: gt.ProductAttributesDefinition

  variantData: Record<
    'color' | 'size' | 'variant' | 'style',
    {
      label?: string | null
      image?: string
    }
  >
  related: {
    crossSells: string[]
    alternatives: string[]
    optionalAdditions: string[]
    configurableRelations?: ConfigurableRelationData[]
  }
  subtitle: string
  stock: number
  deliveryDate: string | null
  specialDelivery: boolean
  sellOut: boolean
  sellable: boolean
  additionalInformation?: {
    list_positon: number
    item_list_name: string
    item_list_id: string
  }
  isMeterware: boolean
  isMainVariant: boolean
  isPartnerItem: boolean
  productReviews: {
    productReviewAvg?: number
    productReviewCount?: number
    productReviewTextCount?: number
  }
}
